<template>
  <div class="default-select"
       :class="{input_type: as_input_type}"
       tabindex="0">
    <span v-if="placeholder" class="default-select_label">
      {{ placeholder }}
    </span>
    <div class="default-select_field"
         :class="{opened, multiple, disabled}"
         @click="changeOpen(!opened)">
      <div class="default-select_field-value"
        :class="{multiple: get_selected_val_arr.length}">
        <template v-if="!has_selected_value">
          <span class="default-select_field-value__placeholder" :title="empty_value_select_text">
            {{ empty_value_select_text }}
          </span>
        </template>
        <template v-else-if="!selected_val_is_array">
          <span :title="get_selected_val">{{ get_selected_val }}</span>
        </template>
        <template v-else>
          <div v-for="item in get_selected_val_arr"
               class="default-select_field-value__item">
            {{ item[display_item_api_key] || item }}
            <span @click.stop="deselectArrayValue(item)">✖</span>
          </div>
        </template>
      </div>
      <span class="default-select_field-deselect"
            v-if="get_allow_deselect"
            @click.stop="deselectValue">✖</span>
      <span class="default-select_field-arrow">
          <span class="default-select_field-arrow__sign" :class="{rotate: opened}"></span>
      </span>
    </div>
    <span v-if="!valid" class="default_input-error_text">
      {{ $t('form_fields.default_empty_input') }}
    </span>
    <div class="default-select_dropdown"
         :style="get_dropdown_styles"
         :class="search_dropdown_classes">
      <div v-if="searchable" class="default-select_dropdown__search">
        <input ref="search_input"
               type="text"
               :value="search_val"
               @input="changeSearch"/>
      </div>
      <div
        ref="dropdown_scroll"
        class="default-select_dropdown__list">
        <div v-for="(item, index) in list_to_show"
             class="default-select_dropdown__item"
             :class="{hovered: index + 1 === focus_on, selected: selected_ids.includes(item.id)}"
             :key="`dropdown_scroll_${uniq_id}_${index}`"
             @click="selectedValue(item)">
          {{item[display_item_api_key] || item}}
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from "axios";
import {ssr_document, ssr_window} from "../../helpers/ssr";

let axiosSource;
const CancelToken = axios.CancelToken;
export default {
  name: "DefaultSelect",
  props: {
    selected_val: null,
    placeholder: {
      type: String,
      default: ''
    },
    // to show name from response list
    display_item_api_key: {
      type: String,
      default: 'title'
    },
    list_outer: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    allow_deselect: {
      type: Boolean,
      default: false
    },
    axios_request: {
      type: Function,
      default: null
    },
    axios_url: {
      type: String,
      default: ''
    },
    axios_data: {
      type: Object,
      default: () => {}
    },
    axios_request_paginated: {
      type: Boolean,
      default: false
    },
    fetch_on_search: {
      type: Boolean,
      default: false
    },
    keep_open_on_select: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    as_input_type: {
      type: Boolean,
      default: false
    },
    validate_f: {
      type: Function,
      default: function() {
        return Boolean(this.selected_val)
      }
    },
    empty_value_select_text: {
      type: String,
      default: ''
    },
    dropdown_position_absolute: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    get_list() {
      return this.list_outer.length ? this.list_outer : this.list
    },
    get_allow_deselect() {
      if (!this.allow_deselect) return false
      return this.has_selected_value && !this.multiple
    },
    has_selected_value() {
      return Boolean(
        (this.selected_val_is_array && this.selected_val.length)
        || (!this.selected_val_is_array && this.selected_val)
      )
    },
    get_selected_val() {
      return this.selected_val[this.display_item_api_key] || this.selected_val
    },
    get_selected_val_arr() {
      if (!this.multiple || !this.selected_val_is_array) return []
      return this.selected_val
    },
    selected_val_is_array() {
      return Array.isArray(this.selected_val)
    },
    lower_case_search() {
      return this.search_val.toLowerCase()
    },
    list_of_objects(){
      if (!this.get_list.length) return false
      return typeof this.get_list[0] === 'object'
    },
    list_to_show() {
      if (this.list_of_objects) {
        if (this.searchable && this.axios_request_paginated) {
          return this.get_list
        }
        return this.get_list.filter(el => {
          return `${el[this.display_item_api_key]}`.toLowerCase().includes(this.lower_case_search)
        })
      }
      return this.get_list.filter(el => el.includes(this.lower_case_search))
    },
    search_dropdown_classes() {
      return {
        opened: this.opened,
        position_absolute: this.dropdown_position_absolute,
      }
    },
    get_dropdown_styles() {
      return {
        top: `${this.dropdown_styles.top}px`,
        left: `${this.dropdown_styles.left}px`,
        width: `${this.dropdown_styles.width}px`,
      }
    },
    uniq_id() {
      return this._uid
    },
    selected_ids() {
      if (!this.selected_val) return []
      if (this.multiple && !this.selected_val_is_array) {
        return this.selected_val.map(el => el.id || el)
      }
      return [this.selected_val.id || this.selected_val]
    },
    is_valid() {
      return this.valid
    },
  },
  data() {
    return {
      valid: true,
      opened: false,
      search_val: '',
      list: [],
      focus_on: 0,
      current_pagination_page: 1,
      last_pagination_page: 1,
      data_loading: false,

      dropdown_styles: {
        top: 0,
        left: 0,
        width: 0
      }
    }
  },
  mounted() {
    if (this.axios_request_paginated) {
      this.$refs.dropdown_scroll.addEventListener('scroll', this.dropdownScrollEvent)
    }
  },
  methods: {
    changeOpen(bool) {
      if (this.disabled) return
      if (bool) {
        this.openDropdown()
      } else {
        this.closeDropdown()
      }
    },
    openDropdown() {
      if (!this.dropdown_position_absolute) {
        this.scrollEvent()
        ssr_window.addEventListener('scroll', this.scrollEvent)
      }
      this.opened = true
      setTimeout(() => {
        this.focusOnSearchInput()
        ssr_document.addEventListener('click', this.clickOutsideEvent)
        if (this.list_to_show.length) return
        this.getAxiosRequest()
      }, 0)
    },
    focusOnSearchInput() {
      this.$refs.search_input?.focus()
    },
    closeDropdown() {
      this.opened = false
      this.removeEventsListener()
    },
    changeSearch(e) {
      this.search_val = e.target.value
      this.$emit('searchField', this.search_val)
      if (!this.fetch_on_search) return
      this.typing(this.searchCallback)
    },
    searchCallback() {
      this.current_pagination_page = 1
      this.getAxiosRequest()
    },
    deselectValue() {
      this.emitData(this.multiple ? [] : null)
    },
    deselectArrayValue(item) {
      if (!this.selected_val_is_array) return
      this.emitData(
        this.list_of_objects
          ? this.selected_val.filter(el => el.id !== item.id)
          : this.selected_val.filter(el => el !== item)
      )
    },
    getAxiosRequest(push_to_list = false) {
      if (!(this.axios_url || this.axios_request)
        || this.current_pagination_page > this.last_pagination_page
      ) return;
      if (axiosSource) {
        axiosSource.cancel('Operation canceled by the user');
      }
      axiosSource = CancelToken.source()
      this.data_loading = true
      if (!push_to_list) {
        this.list = []
      }
      let axios_data = {
        ...this.axios_data,
        ...{
          search: this.search_val,
          page: this.current_pagination_page
        }
      }
      let axios_request = this.axios_request
        ? this.axios_request(axios_data, axiosSource.token)
        : axios.get(this.axios_url, {params: axios_data}, axiosSource.token)
      axios_request.then(({data}) => {
          (data.collection || data || []).map(el => {
            this.list.push(el)
          })
          this.current_pagination_page = data.current_page || 1
          this.last_pagination_page = data.last_page || 1
        })
        .finally(() => {
          this.data_loading = false
        })
    },
    selectedValue(item) {
      let emit_val = item
      if (this.multiple) {
        let selected_val = []
        if (this.has_selected_value) {
          selected_val = [...JSON.parse(JSON.stringify(this.selected_val))]
        }
        if (typeof item !== 'object') {
          item = item.trim()
          selected_val = selected_val.map(el => el.trim());
        }
        if (
          (typeof item === 'object' && !selected_val.some(el => el.id === item.id))
          || (typeof item !== 'object' && !selected_val.some(el => el === item))
        ) {
          selected_val.push(item)
        }
        emit_val = selected_val
      }
      this.valid = true
      this.emitData(emit_val)
    },
    emitData(emit_val) {
      this.$emit('change', emit_val)
      if (!this.keep_open_on_select) {
        this.closeDropdown()
      }
      if (this.multiple && !this.dropdown_position_absolute) {
        // update if height changed — not needed in absolute mode, where the
        // dropdown tracks the field's current height via CSS (top: 100%),
        // and scrollEvent()'s viewport-relative math is wrong here anyway.
        this.$nextTick(() => {
          this.scrollEvent()
        })
      }
    },
    //events
    clickOutsideEvent(e) {
      let closest = e.target.closest('.' + this.$el.className.split(' ').join('.'))
      if (!closest || closest !== this.$el) {
        this.closeDropdown()
      }
    },
    dropdownScrollEvent({target}) {
      if (this.data_loading || target.scrollHeight > target.clientHeight + target.scrollTop + 10) return
      this.current_pagination_page++
      this.getAxiosRequest(true)
    },
    removeEventsListener() {
      ssr_window.removeEventListener('scroll', this.scrollEvent)
      ssr_document.removeEventListener('click', this.clickOutsideEvent)
      this.$refs.dropdown_scroll.removeEventListener('click', this.dropdownScrollEvent)
    },
    scrollEvent() {
      let rect = this.$el.getBoundingClientRect()
      if (!rect) return
      this.dropdown_styles.top = rect.bottom
      this.dropdown_styles.left = rect.left
      this.dropdown_styles.width = rect.width
    },
    updateValid(value) {
      if (!this.required) return
      this.valid = this.validate_f(value)
    },
  },
  beforeDestroy() {
    this.removeEventsListener()
  },
}
</script>
